import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { PRODUCTS } from "@/data/projects";

const SEO = {
  "kunststof-kozijnen": {
    title: "Kunststof Kozijnen op maat | DEKO Kozijnen Noord-Nederland",
    description: "Kunststof kozijnen op maat: optimale isolatie, onderhoudsvrij en 15 jaar garantie. Geplaatst door eigen monteurs in heel Noord-Nederland. Vraag een gratis offerte aan.",
  },
  "deuren": {
    title: "Kunststof Voordeuren & Deuren | DEKO Kozijnen Noord-Nederland",
    description: "Kunststof voordeuren en achterdeuren: inbraakwerend, geïsoleerd en op maat gemaakt. Geplaatst in heel Noord-Nederland met 15 jaar garantie. Vraag een gratis offerte aan.",
  },
  "schuifpuien": {
    title: "Schuifpuien op maat | DEKO Kozijnen Noord-Nederland",
    description: "Hefschuifpuien met maximaal glas en slanke profielen. Drempelloos wonen met optimale isolatie, geplaatst door eigen monteurs in Noord-Nederland. Gratis offerte.",
  },
};

export default function ProductPage({ slug }) {
  const p = PRODUCTS[slug];
  return (
    <main data-testid={`product-${slug}`}>
      <Seo title={SEO[slug].title} description={SEO[slug].description} />
      <section className="relative h-[70vh] min-h-[520px] flex items-end pb-14 pt-32 overflow-hidden">
        <motion.img src={p.hero} alt={p.name} initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 md:px-10 text-white">
          <span className="text-xs uppercase tracking-[0.25em] text-white/60">{p.name}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 max-w-3xl">{p.tagline}</h1>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-8">
            <p className="font-display text-2xl sm:text-3xl font-medium text-[#2F3438] leading-snug max-w-2xl">{p.intro}</p>
          </Reveal>
        </div>
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
          {p.features.map((f, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex gap-4 border-t border-[#ECECEA] pt-6">
                <Check className="h-5 w-5 text-[#2F3438] mt-1 shrink-0" />
                <div>
                  <h3 className="font-display font-semibold text-xl text-[#2F3438]">{f.title}</h3>
                  <p className="text-[#222]/55 mt-2 font-light">{f.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#F7F7F5] py-24">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {p.gallery.map((img, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="overflow-hidden rounded-sm aspect-[4/5] group">
                <img src={img} alt={`${p.name} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-24 text-center">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#2F3438]">Interesse in {p.name.toLowerCase()}?</h2>
            <Link to="/offerte" data-testid="product-offerte-cta" className="group inline-flex items-center gap-3 mt-8 bg-[#2F3438] text-white px-8 py-4 rounded-sm hover:bg-[#222] transition-colors duration-300">
              <span className="text-sm font-medium">Vraag vrijblijvend een offerte aan</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
